"use client";

import Link from "next/link";
import { Reveal } from "@/components/landing/Reveal";

type SurggiePortrait = {
  src: string;
  label: string;
};

const SURGGIE_GRID: SurggiePortrait[] = [
  { src: "/assets/surggies/char_surggie_1.png", label: "Salespup" },
  { src: "/assets/surggies/char_surggie_2.png", label: "Debuggor" },
  { src: "/assets/surggies/char_surggie_3.png", label: "Qubit" },
  { src: "/assets/surggies/char_surggie_4.png", label: "Vibecat" },
  { src: "/assets/surggies/char_surggie_5.png", label: "Opster" },
  { src: "/assets/surggies/char_surggie_6.png", label: "Byteling" },
  { src: "/assets/surggies/char_surggie_7.png", label: "Tidepup" },
  { src: "/assets/surggies/char_surggie_8.png", label: "Coinkit" },
  { src: "/assets/surggies/char_surggie_9.png", label: "Helpfox" },
];

type Highlight = {
  count: string;
  title: string;
  caption: string;
};

const HIGHLIGHTS: Highlight[] = [
  {
    count: "12",
    title: "Surggies to catch",
    caption: "Each with unique skills and tools.",
  },
  {
    count: "4",
    title: "Maps to explore",
    caption: "Beachside HQ, dev labs, trading floor, ops bay.",
  },
  {
    count: "TB",
    title: "Turn-based battles",
    caption: "Classic RPG combat — pick moves, win fights.",
  },
];

// Cute-gold palette — mirrors globals.css :root CSS vars so the game embed
// belongs to the same sunny sticker-book design system as the rest of the page.
const PALETTE = {
  cream: "#FFFDF6",
  sand: "#FBEEC8",
  bronze: "#E0A82E",
  walnut: "#E0A82E",
  ink: "#2B2114",
  gold: "#F5C518",
  moss: "#88C9A1",
  ocean: "#7CC5E8",
} as const;

const PIXELATED: React.CSSProperties = {
  imageRendering: "pixelated",
};

function PortraitTile({ src, label }: SurggiePortrait) {
  return (
    <div
      className="relative flex flex-col items-center gap-2 p-2"
      style={{
        backgroundColor: PALETTE.cream,
        border: `2px solid ${PALETTE.ink}`,
        boxShadow: `inset 1px 1px 0 0 ${PALETTE.cream}, 2px 2px 0 0 ${PALETTE.walnut}`,
      }}
    >
      <div
        className="w-full aspect-square flex items-center justify-center"
        style={{
          backgroundColor: PALETTE.sand,
          border: `1px solid ${PALETTE.walnut}`,
        }}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={src}
          alt={label}
          width={64}
          height={64}
          style={{
            ...PIXELATED,
            width: "80%",
            height: "80%",
            objectFit: "contain",
          }}
        />
      </div>
      <span
        className="font-mono text-[10px] uppercase tracking-wider text-center w-full"
        style={{ color: PALETTE.ink }}
      >
        {label}
      </span>
    </div>
  );
}

export function DemoShowcase() {
  return (
    <section id="demo" className="relative py-24 px-4 bg-cream section-sun">
      <div className="max-w-5xl mx-auto">
        <Reveal>
          <p className="text-center text-[var(--ink-soft)] font-mono text-xs uppercase tracking-widest mb-4">
            See it in action
          </p>
          <h2
            className="text-center text-4xl md:text-5xl font-black text-[var(--ink)] mb-12"
            style={{ fontFamily: "var(--font-manrope)" }}
          >
            A Real <span className="gold-shimmer">Pixel-Art</span> RPG
          </h2>
        </Reveal>

        {/* Cute gold sticker frame — chunky ink outline + gold sticker drop shadow */}
        <Reveal delayMs={120}>
        <div
          className="relative overflow-hidden rounded-3xl"
          style={{
            backgroundColor: PALETTE.cream,
            border: `3px solid ${PALETTE.ink}`,
            boxShadow: `5px 5px 0 var(--gold-deep)`,
          }}
        >
          {/* Faux window chrome — pixel style */}
          <div
            className="flex items-center gap-2 px-3 py-2"
            style={{
              backgroundColor: PALETTE.walnut,
              borderBottom: `2px solid ${PALETTE.ink}`,
            }}
          >
            <div
              className="w-3 h-3"
              style={{ backgroundColor: "#c54a4a", border: `1px solid ${PALETTE.ink}` }}
            />
            <div
              className="w-3 h-3"
              style={{ backgroundColor: PALETTE.gold, border: `1px solid ${PALETTE.ink}` }}
            />
            <div
              className="w-3 h-3"
              style={{ backgroundColor: PALETTE.moss, border: `1px solid ${PALETTE.ink}` }}
            />
            <div
              className="flex-1 mx-3 text-center font-mono text-[11px] py-0.5"
              style={{
                backgroundColor: PALETTE.cream,
                color: PALETTE.ink,
                border: `1px solid ${PALETTE.ink}`,
              }}
            >
              surggies.surgingmarkets.com/game
            </div>
          </div>

          {/* Showcase area: 3x3 sprite grid + side panel */}
          <div
            className="grid grid-cols-1 md:grid-cols-5 gap-6 p-6 md:p-8"
            style={{
              backgroundColor: PALETTE.cream,
              backgroundImage: `
                linear-gradient(${PALETTE.sand}33 1px, transparent 1px),
                linear-gradient(90deg, ${PALETTE.sand}33 1px, transparent 1px)
              `,
              backgroundSize: "16px 16px",
            }}
          >
            {/* 3x3 character portrait grid */}
            <div className="md:col-span-3">
              <div
                className="font-mono text-[11px] uppercase tracking-wider mb-3"
                style={{ color: PALETTE.walnut }}
              >
                Your Surggies Roster
              </div>
              <div className="grid grid-cols-3 gap-3">
                {SURGGIE_GRID.map((s) => (
                  <PortraitTile key={s.label} src={s.src} label={s.label} />
                ))}
              </div>
            </div>

            {/* Battle-scene mock side panel */}
            <div className="md:col-span-2 flex flex-col gap-3">
              <div
                className="font-mono text-[11px] uppercase tracking-wider"
                style={{ color: PALETTE.walnut }}
              >
                Battle Preview
              </div>

              <div
                className="relative aspect-square flex items-end justify-between p-4"
                style={{
                  backgroundColor: PALETTE.moss,
                  border: `2px solid ${PALETTE.ink}`,
                  boxShadow: `inset 1px 1px 0 0 ${PALETTE.cream}40`,
                }}
              >
                {/* Sandy battle floor */}
                <div
                  className="absolute inset-x-0 bottom-0 h-1/3"
                  style={{ backgroundColor: PALETTE.sand, borderTop: `1px solid ${PALETTE.walnut}` }}
                />

                {/* Player Surggie (foreground left) */}
                <div className="relative z-10 flex flex-col items-center">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="/assets/surggies/char_surggie_3.png"
                    alt="Player Surggie"
                    width={64}
                    height={64}
                    style={{ ...PIXELATED, width: 64, height: 64, objectFit: "contain" }}
                  />
                </div>

                {/* Enemy Surggie (background right) */}
                <div className="relative z-10 flex flex-col items-center">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="/assets/surggies/char_surggie_8.png"
                    alt="Enemy Surggie"
                    width={48}
                    height={48}
                    style={{ ...PIXELATED, width: 48, height: 48, objectFit: "contain" }}
                  />
                </div>
              </div>

              {/* HP bar mock */}
              <div
                className="p-2"
                style={{
                  backgroundColor: PALETTE.cream,
                  border: `2px solid ${PALETTE.ink}`,
                }}
              >
                <div className="flex items-center justify-between font-mono text-[10px] uppercase mb-1" style={{ color: PALETTE.ink }}>
                  <span>Qubit</span>
                  <span>Lv. 12</span>
                </div>
                <div
                  className="w-full h-2"
                  style={{ backgroundColor: PALETTE.walnut, border: `1px solid ${PALETTE.ink}` }}
                >
                  <div
                    className="h-full"
                    style={{ width: "72%", backgroundColor: PALETTE.moss }}
                  />
                </div>
                <div className="font-mono text-[9px] mt-1" style={{ color: PALETTE.walnut }}>
                  HP 36 / 50
                </div>
              </div>
            </div>
          </div>
        </div>
        </Reveal>

        {/* Highlight callouts */}
        <Reveal delayMs={80}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
          {HIGHLIGHTS.map((h) => (
            <div
              key={h.title}
              className="p-4 text-center rounded-2xl pop-hover"
              style={{
                backgroundColor: PALETTE.cream,
                border: `3px solid ${PALETTE.ink}`,
                boxShadow: `3px 3px 0 var(--gold-deep)`,
              }}
            >
              <div
                className="font-black text-3xl mb-1"
                style={{ color: PALETTE.gold, fontFamily: "var(--font-manrope)" }}
              >
                {h.count}
              </div>
              <div
                className="font-mono text-xs uppercase tracking-wider mb-1"
                style={{ color: PALETTE.ink }}
              >
                {h.title}
              </div>
              <div className="font-mono text-[11px]" style={{ color: "var(--ink-soft)" }}>
                {h.caption}
              </div>
            </div>
          ))}
        </div>
        </Reveal>

        {/* CTA */}
        <Reveal delayMs={120}>
        <div className="flex flex-col items-center mt-10">
          <Link
            href="/game"
            className="btn-gold sticker-press font-black text-xl px-10 py-4"
            style={{
              fontFamily: "var(--font-manrope)",
              letterSpacing: "0.15em",
              textTransform: "uppercase",
            }}
          >
            Play Now
          </Link>
          <p
            className="mt-4 font-mono text-xs"
            style={{ color: "var(--ink-soft)" }}
          >
            Free to play. No download.
          </p>
        </div>
        </Reveal>
      </div>
    </section>
  );
}
